import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Paper,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  QrCode as QrCodeIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { formatPaymentPageDate } from './paymentPageHelpers';

type PaymentRequest = {
  _id: string;
  amount: number;
  currency: string;
  paymentMethod: 'boleto' | 'pix';
  boleto?: {
    barcode?: string;
    digitableLine?: string;
    dueDate?: string;
    pdfUrl?: string;
    mayPayAfterDue?: boolean;
    cancelPending?: boolean;
  };
  pix?: {
    qrCode?: string;
    qrCodeImage?: string;
    copyPaste?: string;
    expiresAt?: string;
    expired?: boolean;
  };
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
  paidAt?: string;
  createdAt: string;
};

type PaymentMethodSectionProps = {
  pendingPayment: PaymentRequest | null;
  generating: boolean;
  pixExpired: boolean;
  isPixWithId: boolean;
  pixStatus: 'waiting' | 'confirmed' | 'timeout';
  pixCountdown: number;
  pixTotalSeconds: number;
  formatHms: (totalSec: number) => string;
  onGenerateBoleto: () => void;
  onGeneratePix: () => void;
  onOpenBoletoPdf: () => void;
  onCopyToClipboard: (text: string, label: string) => void;
  onConfirmPaymentClick: () => void;
};

// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
export default function PaymentMethodSection({
  pendingPayment,
  generating,
  pixExpired,
  isPixWithId,
  pixStatus,
  pixCountdown,
  pixTotalSeconds,
  formatHms,
  onGenerateBoleto,
  onGeneratePix,
  onOpenBoletoPdf,
  onCopyToClipboard,
  onConfirmPaymentClick,
}: PaymentMethodSectionProps) {
  return !pendingPayment ? (
    <>
      <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Escolha a forma de pagamento:
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4, alignItems: 'stretch' }}>
        <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              cursor: generating ? 'not-allowed' : 'pointer',
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              '&:hover': {
                bgcolor: 'action.hover',
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease',
            }}
            onClick={generating ? undefined : onGenerateBoleto}
          >
            <AccountBalanceIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Boleto Bancário
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                mb: 2,
                minHeight: 0,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Vencimento no dia de pagamento da sua fatura (próximo dia útil se cair em fim de semana ou feriado)
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<DownloadIcon />}
              disabled={generating}
              sx={{ mt: 'auto' }}
            >
              {generating ? 'Gerando...' : 'Gerar Boleto'}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'center',
              cursor: generating ? 'not-allowed' : 'pointer',
              border: '2px solid',
              borderColor: 'success.main',
              borderRadius: 2,
              '&:hover': {
                bgcolor: 'action.hover',
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease',
            }}
            onClick={generating ? undefined : onGeneratePix}
          >
            <QrCodeIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              PIX
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                mb: 2,
                minHeight: 0,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Aprovação imediata
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              fullWidth
              startIcon={<QrCodeIcon />}
              disabled={generating}
              sx={{ mt: 'auto' }}
            >
              {generating ? 'Gerando...' : 'Gerar PIX'}
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </>
  ) : (
    <>
      <Alert severity="info" sx={{ mb: 3 }}>
        {pendingPayment.paymentMethod === 'boleto'
          ? 'Boleto gerado com sucesso! Efetue o pagamento e confirme abaixo. Após o vencimento, o banco emissor pode ainda permitir a liquidação conforme as regras da cobrança.'
          : pixExpired
            ? 'O código PIX expirou ou não é mais válido para pagamento. Gere um novo PIX para continuar.'
            : 'PIX gerado com sucesso! Efetue o pagamento e confirme abaixo.'}
      </Alert>

      {pendingPayment.boleto && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Dados do Boleto
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {pendingPayment.boleto.pdfUrl && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={onOpenBoletoPdf}
                sx={{ mb: 2 }}
              >
                Visualizar Boleto
              </Button>
            </Box>
          )}
          {pendingPayment.boleto.digitableLine && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Linha Digitável
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  value={pendingPayment.boleto.digitableLine}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
                <Tooltip title="Copiar">
                  <IconButton
                    onClick={() => onCopyToClipboard(pendingPayment.boleto!.digitableLine!, 'Linha digitável')}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
          {pendingPayment.boleto.dueDate && (
            <Typography variant="body2" color="text.secondary">
              <strong>Vencimento:</strong> {formatPaymentPageDate(pendingPayment.boleto.dueDate)}
            </Typography>
          )}
        </Paper>
      )}

      {pendingPayment.paymentMethod === 'boleto' && pendingPayment.boleto?.cancelPending && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Cancelamento do boleto enviado ao banco. Esta página será atualizada automaticamente; em seguida você
          poderá gerar o PIX.
        </Alert>
      )}

      {pendingPayment.paymentMethod === 'boleto' && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'success.light',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Prefere pagar via PIX?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Você já possui um boleto gerado, mas pode optar por pagar via PIX para aprovação imediata.
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<QrCodeIcon />}
            onClick={onGeneratePix}
            disabled={generating || !!pendingPayment.boleto?.cancelPending}
          >
            {pendingPayment.boleto?.cancelPending
              ? 'Aguardando banco…'
              : generating
                ? 'Gerando...'
                : 'Gerar PIX agora'}
          </Button>
        </Paper>
      )}

      {pendingPayment.pix && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Dados do PIX
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {pixExpired ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                PIX expirado. Clique em Gerar novo PIX para gerar um novo código.
              </Alert>
              <Button
                variant="contained"
                color="success"
                fullWidth
                startIcon={<QrCodeIcon />}
                onClick={onGeneratePix}
                disabled={generating}
                sx={{ mb: 1 }}
              >
                {generating ? 'Gerando...' : 'Gerar novo PIX'}
              </Button>
            </Box>
          ) : (
            <>
              {pendingPayment.pix.qrCodeImage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <img
                    src={
                      pendingPayment.pix.qrCodeImage.startsWith('data:image/')
                        ? pendingPayment.pix.qrCodeImage
                        : // Inter normalmente retorna PNG base64 (sem prefixo). Se for SVG base64, ajusta o mime.
                          `data:${
                            pendingPayment.pix.qrCodeImage.startsWith('PHN2Zy') ? 'image/svg+xml' : 'image/png'
                          };base64,${pendingPayment.pix.qrCodeImage}`
                    }
                    alt="QR Code PIX"
                    style={{ maxWidth: 200, height: 'auto' }}
                  />
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Código PIX (Copiar e Colar)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={pendingPayment.pix?.copyPaste || ''}
                    InputProps={{
                      readOnly: true,
                    }}
                    size="small"
                  />
                  <Tooltip title="Copiar">
                    <IconButton onClick={() => onCopyToClipboard(pendingPayment.pix?.copyPaste ?? '', 'Código PIX')}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {pendingPayment.pix.expiresAt && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Expira em:</strong> {formatPaymentPageDate(pendingPayment.pix.expiresAt)}
                </Typography>
              )}

              {isPixWithId && pixStatus === 'waiting' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3 }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                      variant="determinate"
                      value={pixTotalSeconds > 0 ? (pixCountdown / pixTotalSeconds) * 100 : 0}
                      size={72}
                      thickness={4}
                      sx={{ color: 'success.main' }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2" component="span" fontWeight={600} color="text.secondary">
                        {formatHms(pixCountdown)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Tempo restante para pagar com este código PIX
                  </Typography>
                </Box>
              )}

              {isPixWithId && pixStatus === 'timeout' && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Ainda não recebemos a confirmação. Se você já efetuou o pagamento, use &quot;Confirmar Pagamento e
                    Ativar Assinatura&quot; para verificar. Se o código PIX expirou ou você ainda não pagou, gere um
                    novo PIX abaixo.
                  </Alert>
                  <Button
                    variant="outlined"
                    color="success"
                    fullWidth
                    startIcon={<QrCodeIcon />}
                    onClick={onGeneratePix}
                    disabled={generating}
                    sx={{ mb: 1 }}
                  >
                    {generating ? 'Gerando...' : 'Gerar novo PIX'}
                  </Button>
                </Box>
              )}

              {pixStatus !== 'timeout' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Após efetuar o pagamento via PIX, confirme abaixo para ativar sua assinatura.
                </Alert>
              )}
            </>
          )}
        </Paper>
      )}

      {pendingPayment.paymentMethod === 'pix' && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'primary.light',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Mudar para Boleto?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Você pode optar por pagar via boleto. O PIX atual será desativado sem possibilidade de reativação.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AccountBalanceIcon />}
            onClick={onGenerateBoleto}
            disabled={generating}
          >
            {generating ? 'Gerando...' : 'Gerar Boleto'}
          </Button>
        </Paper>
      )}

      {!pixExpired && (
        <Button
          fullWidth
          variant="contained"
          color="success"
          size="large"
          startIcon={<CheckCircleIcon />}
          onClick={onConfirmPaymentClick}
          sx={{
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          Confirmar Pagamento e Ativar Assinatura
        </Button>
      )}
    </>
  );
}
